import "@/App.css";
import { useState, useEffect } from "react";
import { ArrowRight, Shield, Globe, Scale, Menu, X } from "lucide-react";
import { Separator } from "@/components/ui/separator";
import logoSrc from "@/assets/logo-sama-global.svg";

const content = {
  en: {
    nav: {
      operations: "Operations",
      governance: "Governance",
      capitalPartners: "Capital Partners",
      contact: "Contact",
    },
    hero: {
      headline1: "Securing Global Trade.",
      headline2: "Structuring the Future.",
      subheadline:
        "Sama Global Holdings, Inc. serves as a strategic link in international commerce, providing structured finance (Trade Finance) and global supply chain management for industrial, agricultural, and energy commodities.",
      cta: "Corporate Inquiry",
    },
    socialProof: {
      delaware: "Delaware-Registered C-Corporation",
      icc: "ICC International Standards",
      kyc: "KYC/AML Compliance Protocols",
    },
    operations: {
      overline: "Core Operations",
      title: "Operations",
      body: "Direct brokerage and structuring of high-volume agro-commodities. We specialize in supply chain optimization and the issuance of financial instruments (DLC/SBLC) to secure cross-border transactions.",
    },
    governance: {
      overline: "Corporate Structure",
      title: "Governance",
      body: "Registered as a Delaware C-Corporation, Sama Global Holdings operates under strict ICC standards. Our institutional framework ensures full KYC/AML compliance for every partner and transaction.",
    },
    capitalPartners: {
      overline: "Strategic Alliances",
      title: "Capital Partners",
      body: "Bridging the gap between tier-one producers and international institutional buyers. We eliminate intermediaries to ensure fair value distribution and transactional efficiency.",
    },
    trackRecord: {
      overline: "Proven Execution",
      title: "Transaction Track Record",
      cases: [
        {
          title: "National-Scale Infrastructure Procurement",
          body: "Structured the end-to-end supply chain and financing for a large-scale national LED lighting project exceeding one million units. Facilitated direct negotiations between Tier-1 Asian manufacturers and South American project developers, securing the transaction through robust performance guarantees.",
        },
        {
          title: "Global Sugar Export Structuring",
          body: "Executed the structuring of a large-scale sugar export contract. Managed the implementation of strict NCNDA protocols and aligned the transaction for settlement via DLC (Documentary Letters of Credit), ensuring zero-risk direct trade between origin producers and international buyers.",
        },
      ],
    },
    footer: {
      overline: "Institutional Contact",
      title: "Corporate Inquiries",
      body: "For institutional inquiries, please contact:",
      copyright: "\u00a9 2026 Sama Global Holdings, Inc. All rights reserved.",
      location: "Sama Global Holdings, Inc. \u2014 Delaware, USA",
    },
  },
  es: {
    nav: {
      operations: "Operaciones",
      governance: "Gobernanza",
      capitalPartners: "Aliados",
      contact: "Contacto",
    },
    hero: {
      headline1: "Asegurando el Comercio Global.",
      headline2: "Estructurando el Futuro.",
      subheadline:
        "Sama Global Holdings, Inc. act\u00faa como un enlace estrat\u00e9gico en el comercio internacional, proporcionando finanzas estructuradas (Trade Finance) y gesti\u00f3n de cadena de suministro global para commodities industriales, agr\u00edcolas y energ\u00e9ticos.",
      cta: "Consulta Corporativa",
    },
    socialProof: {
      delaware: "C-Corporation Registrada en Delaware",
      icc: "Est\u00e1ndares Internacionales ICC",
      kyc: "Protocolos de Cumplimiento KYC/AML",
    },
    operations: {
      overline: "Operaciones Principales",
      title: "Operaciones",
      body: "Corretaje directo y estructuraci\u00f3n de agro-commodities de alto volumen. Especialistas en optimizaci\u00f3n de cadena de suministro y emisi\u00f3n de instrumentos financieros (DLC/SBLC) para asegurar transacciones transfronterizas.",
    },
    governance: {
      overline: "Estructura Corporativa",
      title: "Gobernanza",
      body: "Registrada como una C-Corporation en Delaware, Sama Global Holdings opera bajo estrictos est\u00e1ndares de la ICC. Nuestro marco institucional garantiza el cumplimiento total de protocolos KYC/AML en cada socio y transacci\u00f3n.",
    },
    capitalPartners: {
      overline: "Alianzas Estrat\u00e9gicas",
      title: "Aliados",
      body: "Cerrando la brecha entre productores de primer nivel y compradores institucionales internacionales. Eliminamos intermediarios para asegurar una distribuci\u00f3n de valor justa y eficiencia transaccional.",
    },
    trackRecord: {
      overline: "Ejecuci\u00f3n Comprobada",
      title: "Casos de \u00c9xito",
      cases: [
        {
          title: "Procuraci\u00f3n de Infraestructura a Escala Nacional",
          body: "Estructuraci\u00f3n integral de la cadena de suministro para un proyecto nacional de iluminaci\u00f3n LED superior a un mill\u00f3n de unidades. Facilitamos la negociaci\u00f3n directa entre fabricantes asi\u00e1ticos de Nivel 1 y desarrolladores de proyectos sudamericanos, asegurando la transacci\u00f3n mediante garant\u00edas de cumplimiento.",
        },
        {
          title: "Estructuraci\u00f3n de Exportaci\u00f3n Global de Az\u00facar",
          body: "Ejecuci\u00f3n y estructuraci\u00f3n de un contrato de exportaci\u00f3n de az\u00facar a gran escala. Gestionamos la implementaci\u00f3n de estrictos protocolos NCNDA y alineamos la transacci\u00f3n para su liquidaci\u00f3n a trav\u00e9s de Cartas de Cr\u00e9dito Documentarias (DLC), garantizando un comercio directo y sin riesgos.",
        },
      ],
    },
    footer: {
      overline: "Contacto Institucional",
      title: "Consultas Corporativas",
      body: "Para consultas institucionales, por favor contacte:",
      copyright: "\u00a9 2026 Sama Global Holdings, Inc. Todos los derechos reservados.",
      location: "Sama Global Holdings, Inc. \u2014 Delaware, USA",
    },
  },
};

function App() {
  const [lang, setLang] = useState("en");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const t = content[lang];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollToContact = () => {
    const el = document.getElementById("contact-section");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  const closeMobileMenu = () => setMobileMenuOpen(false);

  return (
    <div className="App min-h-screen bg-white">
      {/* Navbar */}
      <nav
        data-testid="navbar"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? "bg-white border-b border-slate-200 shadow-sm"
            : "bg-transparent border-b border-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-8 lg:px-16 flex items-center justify-between py-8">
          <a
            href="/"
            data-testid="logo"
          >
            <img
              src={logoSrc}
              alt="Sama Global Holdings"
              className="h-[65px] md:h-[110px] w-auto object-contain"
            />
          </a>
          <div className="flex items-center gap-6 lg:gap-10">
            <div className="hidden md:flex items-center gap-10">
              <a href="#operations" data-testid="nav-operations" className="nav-link text-[#001F3F] text-lg lg:text-xl font-semibold tracking-wide">
                {t.nav.operations}
              </a>
              <a href="#governance" data-testid="nav-governance" className="nav-link text-[#001F3F] text-lg lg:text-xl font-semibold tracking-wide">
                {t.nav.governance}
              </a>
              <a href="#capital-partners" data-testid="nav-capital-partners" className="nav-link text-[#001F3F] text-lg lg:text-xl font-semibold tracking-wide">
                {t.nav.capitalPartners}
              </a>
              <a href="#contact-section" data-testid="nav-contact" className="nav-link text-[#607D8B] text-lg lg:text-xl font-semibold tracking-wide">
                {t.nav.contact}
              </a>
            </div>
            {/* Language Toggle */}
            <div data-testid="lang-toggle" className="flex items-center gap-0 text-sm md:text-base lg:text-lg font-semibold tracking-wide">
              <button
                data-testid="lang-en"
                onClick={() => setLang("en")}
                className={`px-2 py-1 transition-opacity ${lang === "en" ? "text-[#001F3F]" : "text-[#607D8B] opacity-50 hover:opacity-80"}`}
              >
                EN
              </button>
              <span className="text-slate-300">|</span>
              <button
                data-testid="lang-es"
                onClick={() => setLang("es")}
                className={`px-2 py-1 transition-opacity ${lang === "es" ? "text-[#001F3F]" : "text-[#607D8B] opacity-50 hover:opacity-80"}`}
              >
                ES
              </button>
            </div>
            <button
              data-testid="mobile-menu-toggle"
              className="md:hidden text-[#001F3F]"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
        {mobileMenuOpen && (
          <div data-testid="mobile-menu" className="md:hidden bg-white border-t border-slate-200 px-8 py-5 flex flex-col gap-5">
            <a href="#operations" onClick={closeMobileMenu} className="text-[#001F3F] text-base font-semibold tracking-wide">{t.nav.operations}</a>
            <a href="#governance" onClick={closeMobileMenu} className="text-[#001F3F] text-base font-semibold tracking-wide">{t.nav.governance}</a>
            <a href="#capital-partners" onClick={closeMobileMenu} className="text-[#001F3F] text-base font-semibold tracking-wide">{t.nav.capitalPartners}</a>
            <a href="#contact-section" onClick={closeMobileMenu} className="text-[#607D8B] text-base font-semibold tracking-wide">{t.nav.contact}</a>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section
        data-testid="hero-section"
        className="hero-mesh-bg pt-48 pb-24 lg:pt-60 lg:pb-32 border-b border-slate-200"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="max-w-4xl">
            <h1
              data-testid="hero-headline"
              className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-none text-[#001F3F]"
            >
              {t.hero.headline1}<br />
              {t.hero.headline2}
            </h1>
            <p
              data-testid="hero-subheadline"
              className="mt-8 text-base lg:text-lg text-[#607D8B] leading-relaxed max-w-2xl"
            >
              {t.hero.subheadline}
            </p>
            <button
              data-testid="corporate-inquiry-btn"
              onClick={scrollToContact}
              className="cta-btn mt-10 inline-flex items-center gap-2 px-6 py-3 border-2 border-[#001F3F] text-[#001F3F] text-sm font-medium tracking-tight"
            >
              {t.hero.cta}
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </section>

      {/* Social Proof Band */}
      <section
        data-testid="social-proof-band"
        className="bg-white border-b border-slate-200 py-8"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-8">
            <div className="flex items-center gap-3">
              <Shield className="w-4 h-4 text-[#607D8B] flex-shrink-0" />
              <span className="text-xs text-[#607D8B] tracking-tight font-medium uppercase">
                {t.socialProof.delaware}
              </span>
            </div>
            <Separator orientation="vertical" className="hidden md:block h-4" />
            <div className="flex items-center gap-3">
              <Globe className="w-4 h-4 text-[#607D8B] flex-shrink-0" />
              <span className="text-xs text-[#607D8B] tracking-tight font-medium uppercase">
                {t.socialProof.icc}
              </span>
            </div>
            <Separator orientation="vertical" className="hidden md:block h-4" />
            <div className="flex items-center gap-3">
              <Scale className="w-4 h-4 text-[#607D8B] flex-shrink-0" />
              <span className="text-xs text-[#607D8B] tracking-tight font-medium uppercase">
                {t.socialProof.kyc}
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Operations Section */}
      <section
        id="operations"
        data-testid="operations-section"
        className="py-24 lg:py-32 border-b border-slate-200"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-0">
            <div className="md:col-span-4 md:pr-12 md:border-r border-slate-200">
              <p className="text-xs text-[#607D8B] font-medium tracking-widest uppercase mb-4">
                {t.operations.overline}
              </p>
              <h2 className="text-2xl sm:text-3xl font-black tracking-tighter text-[#001F3F] leading-tight">
                {t.operations.title}
              </h2>
            </div>
            <div className="md:col-span-8 md:pl-12 mt-8 md:mt-0 flex items-center">
              <p data-testid="operations-body" className="text-base text-[#607D8B] leading-relaxed">
                {t.operations.body}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Governance Section */}
      <section
        id="governance"
        data-testid="governance-section"
        className="py-24 lg:py-32 border-b border-slate-200"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-0">
            <div className="md:col-span-4 md:pr-12 md:border-r border-slate-200">
              <p className="text-xs text-[#607D8B] font-medium tracking-widest uppercase mb-4">
                {t.governance.overline}
              </p>
              <h2 className="text-2xl sm:text-3xl font-black tracking-tighter text-[#001F3F] leading-tight">
                {t.governance.title}
              </h2>
            </div>
            <div className="md:col-span-8 md:pl-12 mt-8 md:mt-0 flex items-center">
              <p data-testid="governance-body" className="text-base text-[#607D8B] leading-relaxed">
                {t.governance.body}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Capital Partners Section */}
      <section
        id="capital-partners"
        data-testid="capital-partners-section"
        className="py-24 lg:py-32 border-b border-slate-200"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-0">
            <div className="md:col-span-4 md:pr-12 md:border-r border-slate-200">
              <p className="text-xs text-[#607D8B] font-medium tracking-widest uppercase mb-4">
                {t.capitalPartners.overline}
              </p>
              <h2 className="text-2xl sm:text-3xl font-black tracking-tighter text-[#001F3F] leading-tight">
                {t.capitalPartners.title}
              </h2>
            </div>
            <div className="md:col-span-8 md:pl-12 mt-8 md:mt-0 flex items-center">
              <p data-testid="capital-partners-body" className="text-base text-[#607D8B] leading-relaxed">
                {t.capitalPartners.body}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Transaction Track Record Section */}
      <section
        id="track-record"
        data-testid="track-record-section"
        className="py-24 lg:py-32 border-b border-slate-200"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <p className="text-xs text-[#607D8B] font-medium tracking-widest uppercase mb-4">
            {t.trackRecord.overline}
          </p>
          <h2 className="text-2xl sm:text-3xl font-black tracking-tighter text-[#001F3F] leading-tight">
            {t.trackRecord.title}
          </h2>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-0">
            {t.trackRecord.cases.map((c, i) => (
              <div
                key={i}
                data-testid={`track-record-case-${i + 1}`}
                className="p-8 border border-slate-200"
              >
                <span className="text-xs text-[#607D8B] font-medium tracking-widest uppercase">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <h3 className="mt-3 text-base font-medium tracking-tight text-[#001F3F] mb-4">
                  {c.title}
                </h3>
                <p className="text-sm text-[#607D8B] leading-relaxed">
                  {c.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer / Contact Section */}
      <footer
        id="contact-section"
        data-testid="contact-section"
        className="bg-[#001F3F] py-20 lg:py-24"
      >
        <div className="max-w-7xl mx-auto px-8 lg:px-16">
          <p className="text-sm text-[#607D8B] font-medium tracking-widest uppercase mb-4">
            {t.footer.overline}
          </p>
          <h2 className="text-2xl sm:text-3xl lg:text-[32px] font-black tracking-tighter text-white leading-tight">
            {t.footer.title}
          </h2>
          <p className="mt-6 text-base text-slate-400 leading-relaxed max-w-xl">
            {t.footer.body}
          </p>
          <a
            href="mailto:trade@samaglobalholdings.com"
            data-testid="contact-email"
            className="mt-4 inline-block text-white text-lg lg:text-xl font-medium tracking-wide border-b border-white/30 pb-1 hover:opacity-70 transition-opacity"
          >
            trade@samaglobalholdings.com
          </a>
          <p data-testid="footer-address" className="mt-6 text-sm md:text-base text-slate-400 tracking-wide leading-relaxed">
            30 Wall Street, 8th Floor, New York, NY 10005, USA
          </p>
          <div className="mt-16 pt-8 border-t border-white/10 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <img
              src={logoSrc}
              alt="Sama Global Holdings"
              data-testid="footer-logo"
              className="h-[65px] md:h-[110px] w-auto object-contain brightness-0 invert opacity-50"
            />
            <span data-testid="footer-copyright" className="text-sm md:text-base text-slate-400 tracking-wide">
              {t.footer.copyright}
            </span>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
